import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiSwapBalance, ApiSwapBalanceResign } from "@/api";
import {
  BalanceSwapResultCode,
  type BalanceSwapRequest,
  type BalanceSwapResponse,
} from "@/api/types";

export class BalanceSwapBusinessError extends Error {
  resultCode: number;

  constructor(resultCode: number, msg?: string) {
    super(msg || `SwapBalance failed with resultCode ${resultCode}`);
    this.name = "BalanceSwapBusinessError";
    this.resultCode = resultCode;
  }
}

export const BALANCE_SWAP_RESULT_I18N_KEY: Record<
  Exclude<BalanceSwapResultCode, BalanceSwapResultCode.Success>,
  string
> = {
  [BalanceSwapResultCode.PendingOrder]: "toast.swap.pendingOrder",
  [BalanceSwapResultCode.InvalidAmount]: "toast.swap.amountInvalid",
  [BalanceSwapResultCode.InsufficientBalance]: "toast.swap.insufficientBalance",
  [BalanceSwapResultCode.SignFailed]: "toast.swap.signFailed",
  [BalanceSwapResultCode.InvalidSide]: "toast.swap.sideInvalid",
};

export function mapBalanceSwapResultCodeToI18nKey(resultCode: number): string {
  const key =
    BALANCE_SWAP_RESULT_I18N_KEY[
      resultCode as keyof typeof BALANCE_SWAP_RESULT_I18N_KEY
    ];
  return key ?? "toast.swap.failed";
}

function assertBalanceSwapSuccess(data: BalanceSwapResponse) {
  const code = Number(data.resultCode ?? BalanceSwapResultCode.Success);
  if (code !== BalanceSwapResultCode.Success) {
    throw new BalanceSwapBusinessError(code, data.msg);
  }
  if (!data.swap) {
    throw new Error(data.msg || "SwapBalance response missing swap payload");
  }
}

export function useSwapBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: BalanceSwapRequest
    ): Promise<BalanceSwapResponse> => {
      const res = await ApiSwapBalance(data);
      assertBalanceSwapSuccess(res.data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["balanceV2"] });
    },
  });
}

export function useSwapBalanceResign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<BalanceSwapResponse> => {
      const res = await ApiSwapBalanceResign();
      assertBalanceSwapSuccess(res.data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["balanceV2"] });
    },
  });
}
